//==============================================================================
// PowerReviews
//
// PowerReviews integration for product pages and product listing pages
//==============================================================================
import * as React from 'react';
import classnames from 'classnames';

import { clsHelper } from '../../utilities/class-name-helper';
import { injectScript } from '../../utilities/inject-helper';

import { IPowerReviewsData } from './power-reviews.data';
import { IPowerReviewsProps, displayType } from './power-reviews.props.autogenerated';

//==============================================================================
// CLASS NAME UTILITY
//==============================================================================
const BASE_CLASS = 'pr';
const cls = (fragment?: string) => clsHelper(BASE_CLASS, fragment, '-');
const prClass = {
    questionDisplay: cls('questiondisplay'),
    questionSnippet: cls('questionsnippet'),
    reviewDisplay: cls('reviewdisplay'),
    reviewSnippet: cls('reviewsnippet'),
    write: cls('write')
};

//==============================================================================
// CLASS DEFINITION
//==============================================================================
/**
 * PowerReviews component
 * @extends {React.PureComponent<IPowerReviewsProps<IPowerReviewsData>>}
 */
//==============================================================================
class PowerReviews extends React.PureComponent<IPowerReviewsProps<IPowerReviewsData>> {
    //==========================================================================
    // PUBLIC METHODS
    //==========================================================================
    //------------------------------------------------------
    // Invoked immediately after component is mounted
    //------------------------------------------------------
    public componentDidMount(): void {
        const { config, data } = this.props;

        if (typeof window !== 'undefined' && config.url) {
            injectScript(config.url);

            // @ts-ignore: pwr added through script
            window.pwr = window.pwr || function() {
                // @ts-ignore: pwr added through script
                (pwr.q = pwr.q || []).push(arguments);
            };

            const prCommon = {
                api_key: config.apiKey,
                locale: config.locale,
                merchant_group_id: config.groupId,
                merchant_id: config.merchantId
            };

            if (config.displayType === displayType.questionsReviews) {
                // @ts-ignore: pwr added through script
                pwr('render', {
                    ...prCommon,
                    page_id: `${data.product.result?.ItemId}_${config.siteId}`,
                    review_wrapper_url: `${config.reviewPage?.linkUrl.destinationUrl}?pr_page_id=${data.product.result?.ItemId}_${config.siteId}`,
                    //*** Start Feedless Product Code ***
                    product: {
                        name: data.product.result?.Name,
                        url: window.location.href,
                        image_url: data.product.result?.PrimaryImageUrl,
                        description: data.product.result?.Description,
                        category_name: data.categoryPaths.result?.length && data.categoryPaths.result[0].Name,
                        price: data.product.result?.Price
                    },
                    //*** End Feedless Product Code ***
                    components: {
                        QuestionDisplay: prClass.questionDisplay,
                        QuestionSnippet: prClass.questionSnippet,
                        ReviewDisplay: prClass.reviewDisplay,
                        ReviewSnippet: prClass.reviewSnippet
                    }
                });
            }

            if (config.displayType === displayType.writeReview) {
                // @ts-ignore: pwr added through script
                pwr('render', {
                    ...prCommon,
                    on_submit: function() {
                        window.scrollTo(0, 0);
                    },
                    components: {
                        Write: prClass.write
                    }
                });
            }
        }
    }

    //------------------------------------------------------
    // Render function
    //------------------------------------------------------
    public render(): JSX.Element {
        const { config } = this.props;

        return (
            <div className={classnames(BASE_CLASS, config.className)}>
                {config.displayType === displayType.questionsReviews && (
                    <>
                        <div id={prClass.questionDisplay} />
                        <div id={prClass.reviewDisplay} />
                    </>
                )}
                {config.displayType === displayType.writeReview && (
                    <div id={prClass.write} />
                )}
                {config.displayType === displayType.reviewSnippet && (
                    <div className={cls('reviewsnippet')}>
                        <div id={prClass.reviewSnippet} />
                        {config.reviewSnippetLink &&
                            <a
                                className={cls('reviewsnippet-link')}
                                title={config.reviewSnippetLink.linkText}
                                href={config.reviewSnippetLink.linkUrl?.destinationUrl}
                                aria-label={config.reviewSnippetLink.ariaLabel}
                                target={config.reviewSnippetLink.openInNewTab ? '_blank' : undefined}
                                rel='noreferrer'
                                role='button'
                            >
                                {config.reviewSnippetLink.linkText}
                            </a>
                        }
                    </div>
                )}
            </div>
        );
    }
}

export default PowerReviews;
